import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { City } from "@/apis/city/city-apis";
import type { Whitelabel } from "@/types/reducer-states";

interface CityState {
  savedCity: string | null;
  savedCityFullInfo: City | null;
  savedWhitelabel: number | null;
  savedWhitelabelFullInfo: Whitelabel | null;
  savedPartnerId: number | null;

  citiesList: City[];
  whitelabels: Whitelabel[];

  selectCity: (cityCode: string | null, cityInfo?: City | null) => void;
  selectWhitelabel: (
    whitelabelId: number | null,
    whitelabelInfo?: Whitelabel | null
  ) => void;
  selectPartnerId: (partnerId: number | null) => void;
  setCitiesList: (cities: City[]) => void;
  setWhitelabels: (whitelabels: Whitelabel[]) => void;
  clearSavedCity: () => void;
  clearAll: () => void;
}

export const useCityStore = create<CityState>()(
  persist(
    (set) => ({
      savedCity: null,
      savedCityFullInfo: null,
      savedWhitelabel: null,
      savedWhitelabelFullInfo: null,
      savedPartnerId: null,
      citiesList: [],
      whitelabels: [],

      selectCity: (cityCode, cityInfo = null) =>
        set({
          savedCity: cityCode,
          savedCityFullInfo: cityInfo,
        }),

      selectWhitelabel: (whitelabelId, whitelabelInfo = null) =>
        set({
          savedWhitelabel: whitelabelId,
          savedWhitelabelFullInfo: whitelabelInfo,
        }),

      selectPartnerId: (partnerId) => set({ savedPartnerId: partnerId }),

      setCitiesList: (cities) => set({ citiesList: cities }),

      setWhitelabels: (whitelabels) => set({ whitelabels }),

      clearSavedCity: () =>
        set({
          savedCity: null,
          savedCityFullInfo: null,
        }),

      clearAll: () =>
        set({
          savedCity: null,
          savedCityFullInfo: null,
          savedWhitelabel: null,
          savedWhitelabelFullInfo: null,
          savedPartnerId: null,
          citiesList: [],
          whitelabels: [],
        }),
    }),
    {
      name: "city-store",
      partialize: (state) => ({
        savedCity: state.savedCity,
        savedWhitelabel: state.savedWhitelabel,
        savedPartnerId: state.savedPartnerId,
        // Don't persist full info or lists - they're fetched fresh
      }),
    }
  )
);

export const useSavedCity = () => useCityStore((state) => state.savedCity);
export const useSavedCityFullInfo = () =>
  useCityStore((state) => state.savedCityFullInfo);
export const useSavedWhitelabel = () =>
  useCityStore((state) => state.savedWhitelabel);
export const useSavedPartnerId = () =>
  useCityStore((state) => state.savedPartnerId);
export const useCitiesList = () => useCityStore((state) => state.citiesList);
export const useWhitelabelsList = () =>
  useCityStore((state) => state.whitelabels);
